import { Alert, Button, Heading, PasswordField } from '@aws-amplify/ui-react';
import { confirmResetPassword } from 'aws-amplify/auth';
import { components as authComponents } from 'components/forms/AuthComponents';
import { type FormEvent, type ReactNode, useMemo, useState } from 'react';

import ResetPasswordAlertMessage from './ResetPasswordAlertMessage';
import {
    defaultResetPasswordMessages,
    type ResetPasswordAlertCopy,
    type ResetPasswordMessages,
} from './resetPasswordMessages';

import './styles/ResetPasswordPage.scss';

const getCodeFromLocation = () => {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const searchParams = new URLSearchParams(window.location.search);
    return hashParams.get('code') ?? searchParams.get('code') ?? '';
};

interface PasswordError {
    message: ResetPasswordAlertCopy;
    canRetry?: boolean;
}

interface ResetPasswordPageProps {
    confirmResetPasswordHandler?: typeof confirmResetPassword;
    initialCode?: string;
    initialIsComplete?: boolean;
    initialPasswordError?: PasswordError;
    initialUsername?: string;
    messagesOverride?: ResetPasswordMessages;
    onGoToSignIn?: () => void;
}

const getPasswordError = (error: unknown, messages: ResetPasswordMessages): PasswordError => {
    const errorName = error instanceof Error ? error.name : undefined;
    const { alerts } = messages;

    if (errorName === 'CodeMismatchException') {
        return { message: alerts.invalidCodeError };
    }

    if (errorName === 'ExpiredCodeException') {
        return { message: alerts.expiredLinkError };
    }

    if (errorName === 'InvalidPasswordException') {
        return {
            message: alerts.passwordPolicyError,
            canRetry: true,
        };
    }

    return { message: alerts.defaultResetError };
};

interface ErrorAlertProps {
    children: ReactNode;
    onDismiss?: () => void;
}

const ErrorAlert = ({ children, onDismiss }: ErrorAlertProps) => (
    <Alert
        className='ResetPasswordPage__alert'
        variation='error'
        isDismissible={Boolean(onDismiss)}
        onDismiss={onDismiss}
    >
        {children}
    </Alert>
);

const SuccessAlert = ({ children }: { children: ReactNode }) => (
    <Alert className='ResetPasswordPage__alert' variation='success'>
        {children}
    </Alert>
);

interface TerminalErrorStateProps {
    error: PasswordError;
    goToSignInText: string;
    onGoToSignIn: () => void;
}

const TerminalErrorState = ({ error, goToSignInText, onGoToSignIn }: TerminalErrorStateProps) => (
    <div className='ResetPasswordPage__state' data-amplify-form>
        <ErrorAlert>
            <ResetPasswordAlertMessage message={error.message} />
        </ErrorAlert>
        <Button isFullWidth variation='primary' onClick={onGoToSignIn}>
            {goToSignInText}
        </Button>
    </div>
);

const ResetPasswordPage = ({
    confirmResetPasswordHandler = confirmResetPassword,
    initialCode,
    initialIsComplete = false,
    initialPasswordError,
    initialUsername,
    messagesOverride,
    onGoToSignIn,
}: ResetPasswordPageProps = {}) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState<PasswordError | undefined>(initialPasswordError);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isComplete, setIsComplete] = useState(initialIsComplete);
    const messages = messagesOverride ?? defaultResetPasswordMessages;

    const { username, code } = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return {
            username: initialUsername ?? params.get('username') ?? '',
            code: initialCode ?? getCodeFromLocation(),
        };
    }, [initialCode, initialUsername]);

    const isLinkInvalid = !username || !code;
    const terminalError = isLinkInvalid
        ? { message: messages.alerts.missingLinkError }
        : passwordError?.canRetry
          ? undefined
          : passwordError;
    const retryableError = passwordError?.canRetry ? passwordError : undefined;
    const AuthHeader = authComponents.Header;
    const AuthFooter = authComponents.Footer;
    const goToSignIn = () => {
        if (onGoToSignIn) {
            onGoToSignIn();
            return;
        }

        window.location.assign('/');
    };

    const submitPassword = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!password || !confirmPassword) {
            return;
        }

        if (password !== confirmPassword) {
            setPasswordError({
                message: messages.alerts.passwordMismatchError,
                canRetry: true,
            });
            return;
        }

        setPasswordError(undefined);

        if (isLinkInvalid) {
            return;
        }

        setIsSubmitting(true);

        try {
            await confirmResetPasswordHandler({
                username,
                confirmationCode: code,
                newPassword: password,
            });
            setPassword('');
            setConfirmPassword('');
            setIsComplete(true);
        } catch (error) {
            setPasswordError(getPasswordError(error, messages));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <main className='ResetPasswordPage' data-amplify-authenticator data-variation='default'>
            <section data-amplify-container aria-labelledby='reset-password-title'>
                <AuthHeader />
                <div data-amplify-router>
                    <Heading
                        id='reset-password-title'
                        className='ResetPasswordPage__title'
                        level={3}
                        padding='var(--amplify-space-xl) 0 0 var(--amplify-space-xl)'
                    >
                        Reset password
                    </Heading>
                    {isComplete ? (
                        <div className='ResetPasswordPage__state' data-amplify-form role='status'>
                            <SuccessAlert>
                                <ResetPasswordAlertMessage message={messages.alerts.resetSuccess} />
                            </SuccessAlert>
                            <Button isFullWidth variation='primary' onClick={goToSignIn}>
                                Go to Sign in
                            </Button>
                        </div>
                    ) : terminalError ? (
                        <TerminalErrorState
                            error={terminalError}
                            goToSignInText='Go to Sign in'
                            onGoToSignIn={goToSignIn}
                        />
                    ) : (
                        <form className='ResetPasswordPage__form' data-amplify-form onSubmit={submitPassword}>
                            <PasswordField
                                autoComplete='new-password'
                                id='new-password'
                                isDisabled={isSubmitting}
                                isRequired
                                label='New Password'
                                name='new-password'
                                placeholder='New Password'
                                showPasswordButtonLabel='Show New Password'
                                value={password}
                                onChange={event => setPassword(event.target.value)}
                            />
                            <PasswordField
                                autoComplete='new-password'
                                id='confirm-password'
                                isDisabled={isSubmitting}
                                isRequired
                                label='Confirm Password'
                                name='confirm-password'
                                placeholder='Confirm Password'
                                showPasswordButtonLabel='Show Confirm Password'
                                value={confirmPassword}
                                onChange={event => setConfirmPassword(event.target.value)}
                            />
                            {retryableError ? (
                                <ErrorAlert onDismiss={() => setPasswordError(undefined)}>
                                    <ResetPasswordAlertMessage message={retryableError.message} />
                                </ErrorAlert>
                            ) : null}
                            <Button
                                isDisabled={isSubmitting}
                                isFullWidth
                                isLoading={isSubmitting}
                                loadingText='Submitting'
                                type='submit'
                                variation='primary'
                            >
                                Submit
                            </Button>
                        </form>
                    )}
                </div>
                <footer data-amplify-footer>
                    <AuthFooter />
                </footer>
            </section>
        </main>
    );
};

export default ResetPasswordPage;
